import axiosClient from "../axiosClient";
import {
  isBonusChallenge,
  isBonusUnlocked,
  isProTipChallenge,
  loadChallenges,
  refreshChallenges,
} from "../challenges/api_v1_challenges";
import {
  announceEggReward,
  claimedRewardIsNew,
  hasRewardBaseline,
  rememberClaimedRewards,
  rewardTaskId,
} from "../../utils/eggReward";
import {
  balanceAfterReward,
  persistEasterBalance,
  readEasterBalance,
} from "../economy/api_v1_economy";

const EGG_AWARDED_ONCE_KEY = "ga_protip_challenge_eggs_once";
const BONUS_EGGS_ONCE_KEY = "ga_protip_challenge_bonus_once";

function withBearer() {
  const bearerTokenHeader = sessionStorage.getItem("accessToken");
  axiosClient.defaults.headers.common["Authorization"] =
    `Bearer ${bearerTokenHeader}`;
  return axiosClient;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function unwrap(payload) {
  if (!payload || typeof payload !== "object") return null;
  const nested = payload.data;
  if (
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (nested.uuid != null ||
      nested.video != null ||
      nested.seen != null ||
      nested.eggs_awarded != null ||
      nested.reward_available != null ||
      nested.reward_eggs != null ||
      nested.bonus_eggs != null)
  ) {
    return {
      ...nested,
      success: payload.success !== false && nested.success !== false,
      message: nested.message ?? payload.message,
    };
  }
  return payload;
}

/** GET /pro-tip — today's tip card + player seed. */
export function getProTip() {
  return withBearer().get("/pro-tip");
}

/** GET /pro-tip/view/:uuid — called when the tip player opens. */
export function viewProTip(uuid) {
  return withBearer().get(`/pro-tip/view/${uuid}`);
}

/** GET /pro-tip/watch/:uuid — called when the tip is fully watched. */
export function watchProTip(uuid) {
  return withBearer().get(`/pro-tip/watch/${uuid}`);
}

export function normalizeProTip(payload) {
  const root = unwrap(payload);
  if (!root || typeof root !== "object") return null;
  if (payload?.success === false || root.success === false) return null;

  const uuid = root.uuid == null ? "" : String(root.uuid);
  if (!uuid) return null;

  return {
    success: true,
    uuid,
    skillLevel: root.skill_level == null ? "" : String(root.skill_level),
    thumbnail: typeof root.thumbnail === "string" ? root.thumbnail : "",
    video: typeof root.video === "string" ? root.video : "",
    duration: Math.max(0, toNumber(root.duration, 0)),
    isWatched: Boolean(root.is_watched),
    isLiked: Boolean(root.is_liked),
    like: root.is_liked ? 1 : 0,
    rewardAvailable: Boolean(root.reward_available),
    rewardEggs: Math.max(0, toNumber(root.reward_eggs, 0)),
    totalLikes: Math.max(0, toNumber(root.total_likes, 0)),
    views: Math.max(0, toNumber(root.views, 0)),
    message: String(root.message || payload?.message || ""),
  };
}

export function normalizeProTipView(payload) {
  const root = unwrap(payload);
  if (!root || typeof root !== "object") return null;

  return {
    success: root.success !== false,
    message: String(root.message || ""),
    uuid: root.uuid == null ? "" : String(root.uuid),
    isLiked: Boolean(root.is_liked),
    like: root.is_liked ? 1 : 0,
    thumbnail: typeof root.thumbnail === "string" ? root.thumbnail : "",
    video: typeof root.video === "string" ? root.video : "",
    totalLikes: Math.max(0, toNumber(root.total_likes, 0)),
    views: Math.max(0, toNumber(root.views, 0)),
    duration: Math.max(0, toNumber(root.duration, 0)),
    rewardAvailable: Boolean(root.reward_available),
    rewardEggs: Math.max(0, toNumber(root.reward_eggs, 0)),
  };
}

export function normalizeProTipWatch(payload) {
  const root = unwrap(payload);
  if (!root || typeof root !== "object") return null;

  return {
    success: root.success !== false,
    message: String(root.message || ""),
    uuid: root.uuid == null ? "" : String(root.uuid),
    seen: Boolean(root.seen),
    eggsAwarded: Math.max(0, toNumber(root.eggs_awarded, 0)),
    bonusEggs: Math.max(0, toNumber(root.bonus_eggs, 0)),
  };
}

function claimOnce(storageKey, amount) {
  const awarded = Math.max(0, toNumber(amount, 0));
  if (!awarded) return 0;
  try {
    if (sessionStorage.getItem(storageKey) === "1") return 0;
    sessionStorage.setItem(storageKey, "1");
  } catch {
    return awarded;
  }
  return awarded;
}

function proTipEggsAlreadyTaken() {
  try {
    return sessionStorage.getItem(EGG_AWARDED_ONCE_KEY) === "1";
  } catch {
    return false;
  }
}

function proTipChallengeTask(loaded) {
  return (loaded?.tasks || []).find((task) => isProTipChallenge(task));
}

export async function loadProTip() {
  const response = await getProTip();
  return normalizeProTip(response?.data);
}

/**
 * Open tip player — /pro-tip/view returns duration + reward metadata only.
 * Eggs are awarded on /pro-tip/watch after the video is watched.
 */
export async function recordProTipView(uuid) {
  if (!uuid) return null;
  const response = await viewProTip(uuid);
  return normalizeProTipView(response?.data);
}

/**
 * Complete pro tip watch.
 * Egg modal ONLY when this watch newly awards the challenge reward.
 * Snapshot challenges BEFORE /pro-tip/watch — after watch, claimed is already true.
 * If reward already claimed / not available → silent (no modal).
 */
export async function recordProTipWatch(uuid, options = {}) {
  if (!uuid) return null;

  let priorTask = null;
  let priorTasks = [];
  try {
    const prior = await loadChallenges();
    if (!hasRewardBaseline()) rememberClaimedRewards(prior?.tasks);
    priorTasks = prior?.tasks || [];
    priorTask = proTipChallengeTask(prior);
  } catch (error) {
    console.error("Pro tip challenge snapshot failed:", error);
  }

  const claimedBefore =
    Boolean(priorTask?.claimed) || proTipEggsAlreadyTaken();
  const expectedEggs = Math.max(0, toNumber(options.expectedRewardEggs, 0));

  const response = await watchProTip(uuid);
  const result = normalizeProTipWatch(response?.data);
  if (!result || result.success === false) return result;

  const before = readEasterBalance();
  const eggs = claimedBefore
    ? 0
    : claimOnce(EGG_AWARDED_ONCE_KEY, result.eggsAwarded);
  const bonusReady = isBonusUnlocked(
    priorTasks.map((task) =>
      isProTipChallenge(task) ? { ...task, claimed: true } : task
    )
  );
  const bonus =
    claimedBefore || !bonusReady
      ? 0
      : claimOnce(BONUS_EGGS_ONCE_KEY, result.bonusEggs);
  const credited = eggs + bonus;
  result.eggsCredited = credited;
  result.bonusCredited = bonus;

  let loaded = null;
  if (result.seen || credited > 0 || !claimedBefore) {
    try {
      loaded = await refreshChallenges();
      const task = proTipChallengeTask(loaded);
      if (task) {
        result.challenge = {
          ...task,
          claimed: Boolean(task.claimed),
        };
      }
    } catch (error) {
      console.error("Pro tip challenge sync failed:", error);
    }
  }

  const afterRefresh = readEasterBalance();
  const nextBalance = credited
    ? balanceAfterReward(before, credited, afterRefresh)
    : Math.max(before, afterRefresh);
  if (nextBalance !== afterRefresh) {
    result.eggBalance = persistEasterBalance(nextBalance);
  } else {
    result.eggBalance = afterRefresh;
  }

  const task = result.challenge || proTipChallengeTask(loaded);
  const newlyClaimed =
    !claimedBefore &&
    (result.eggsAwarded > 0 ||
      Boolean(task?.claimed) ||
      (expectedEggs > 0 && result.seen) ||
      (task?.claimed && claimedRewardIsNew(task)));
  const modalEggs = newlyClaimed
    ? Math.max(
        eggs,
        result.eggsAwarded,
        Number(task?.rewardEggs) || 0,
        expectedEggs,
        Number(priorTask?.rewardEggs) || 0,
      )
    : 0;

  if (modalEggs > 0 && newlyClaimed) {
    if (eggs === 0 && modalEggs > 0 && !proTipEggsAlreadyTaken()) {
      claimOnce(EGG_AWARDED_ONCE_KEY, modalEggs);
      result.eggBalance = persistEasterBalance(
        balanceAfterReward(before, modalEggs, afterRefresh),
      );
    }
    announceEggReward({
      title: task?.title || priorTask?.title || "1-Min Pro Tip",
      rewardEggs: modalEggs,
      eggBalance: result.eggBalance,
      taskKey: rewardTaskId(task) || rewardTaskId(priorTask) || "pro-tip",
    });
  }
  if (bonus > 0) {
    const bonusTask = (loaded?.tasks || []).find((item) =>
      isBonusChallenge(item),
    );
    announceEggReward({
      title: bonusTask?.title || "Complete all Tasks",
      rewardEggs: bonusTask?.rewardEggs || bonus,
      eggBalance: result.eggBalance,
      taskKey: rewardTaskId(bonusTask) || "bonus-tasks",
    });
  } else if (newlyClaimed) {
    const bonusTask = (loaded?.tasks || []).find((item) =>
      isBonusChallenge(item),
    );
    if (
      bonusTask?.claimed &&
      bonusTask.rewardEggs > 0 &&
      claimedRewardIsNew(bonusTask)
    ) {
      announceEggReward({
        title: bonusTask.title || "Complete all Tasks",
        rewardEggs: bonusTask.rewardEggs,
        eggBalance: result.eggBalance,
        taskKey: rewardTaskId(bonusTask) || "bonus-tasks",
      });
    }
  }

  return result;
}
